import React, { useCallback, useEffect, useRef, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Guid } from "@/Common/Models/Guid";
import { PaginationSettings } from "@/Common/Models/PaginationSettings";
import { PatientBillingHistoryOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BillingProvider } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { GetDiagnosticAndFeeCodeDetailsResponse } from "@/Patient/Api/Dto/Responses/MedicalNote/PatientBillingHistory/GetDiagnosticAndServiceFeeCodesResponse";
import { GetExternalBillingClaimsByPatientResponse } from "@/Patient/Api/Dto/Responses/MedicalNote/PatientBillingHistory/GetExternalBillingClaimsByPatientResponse";

import style from "./PatientBillingHistory.module.css";

import Img_icon_table_downarrow from "@/Common/Components/Tables/_images/icons/table-downarrow.svg";
import Img_icon_table_uparrow from "@/Common/Components/Tables/_images/icons/table-uparrow.svg";

class ClaimsWithProviderName extends ExternalBillingClaimDto {
    public ProviderDisplayName: string;
    constructor(externalBillingClaimDto: ExternalBillingClaimDto) {
        super(externalBillingClaimDto);
        this.ProviderDisplayName = Employee.getFullNameWithPrefixContactFacet(this.CreatedForEmployeeId ?? this.CreatedByEmployeeId);
    }
}

class CodeDetails {
    public diagnosticCodeNames: string[] = [];
    public serviceFeeDescriptions: string[] = [];
}

interface IPatientBillingHistoryProps {
    isReadOnly: boolean;
    encounterSiteId: Guid;
}

function PatientBillingHistory(props: IPatientBillingHistoryProps): JSX.Element {
    const { getText } = useTranslation();
    const [billingClaims, setBillingClaims] = useState<ClaimsWithProviderName[] | null>(null);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [codeDetails, setCodeDetails] = useState<CodeDetails>(new CodeDetails());
    const sortDirectionRef = useRef<SortDirection>(SortDirection.DESC);
    const [paginationSettings, setPaginationSettings] = useState<PaginationSettings<PatientBillingHistoryOrderType>>(
        new PaginationSettings<PatientBillingHistoryOrderType>(PatientBillingHistoryOrderType.ClaimDate)
    );

    const versionRef = useRef<number>(-1);
    const contextRef = useRef<string | null>(null);

    const overdueClaimDetails: { code: string, date: Date, providerClaimId: string }[] = [];

    const allDiagnosticCodesWithDates: { code: string, date: Date, providerClaimId: string }[] = [];
    billingClaims?.forEach(claim => claim.DiagnosticCodes?.forEach(code => allDiagnosticCodesWithDates.push({ code, date: claim.ClaimDate, providerClaimId: claim.ProviderClaimId })));
    allDiagnosticCodesWithDates
        .groupBy(claimInfo => claimInfo.code)
        .forEach(group => {
            const sortedGroup = group.sortByPropertyDesc(claim => claim.date);
            const latestClaim = sortedGroup.firstOrDefault();

            if (DateOnly.parseUnlessNull(latestClaim.date) < DateOnly.today().plus({ years: -1 }))
                overdueClaimDetails.push(latestClaim);
        });

    const uniqueDiagnosticCodesRef = useRef<string[]>([]);
    const uniqueServiceFeeCodesRef = useRef<string[]>([]);

    const loadClaims = useCallback(
        async (paginationSettings: PaginationSettings<PatientBillingHistoryOrderType>) => {
            versionRef.current = patientStore.PatientUpdateBillingHistorySignal;
            contextRef.current = patientStore.getCurrentPatient().InactivableResponse.Id;

            const response = await Client.postAsync<GetExternalBillingClaimsByPatientResponse>(
                ServerOperations.Server.ExternalBilling_GetClaimsByPatientContactFacetId,
                {
                    PatientContactFacetId: patientStore.getCurrentPatient().InactivableResponse.Id,
                    PaginationSettings: paginationSettings
                }
            );

            if (response!.isSuccess) {
                setBillingClaims(response.data.Claims.map(claim => new ClaimsWithProviderName(claim)));
                setPaginationSettings({ ...response.data.PaginationSettings });
            }
        },
        []
    );

    const loadDiagnosticCodeAndServiceFeeDetails = useCallback(
        async (
            icd9Codes: string[],
            serviceFeeCodes: string[]
        ): Promise<GetDiagnosticAndFeeCodeDetailsResponse> => {
            if (!icd9Codes?.any())
                return;

            const response = await Client.postAsync<GetDiagnosticAndFeeCodeDetailsResponse>(
                ServerOperations.Server.MedfarDictionary_GetDiagnosticAndFeeCodeDetails,
                {
                    DiagnosticCodes: icd9Codes,
                    ServiceFeeCodes: serviceFeeCodes,
                    EncounterSiteId: props.encounterSiteId
                }
            );

            if (!response.isSuccess)
                return;

            return response.data;
        },
        []
    );

    useOnStoreChange(
        patientStore,
        () => {
            if (
                versionRef.current !== patientStore.PatientUpdateBillingHistorySignal
                || contextRef.current !== patientStore.getCurrentPatient().InactivableResponse.Id
            ) {
                setBillingClaims(null); // setting claims to null triggers a reload;
            }
        }
    );

    useEffect(
        () => {
            if (billingClaims === null)
                loadClaims(paginationSettings);
        },
        [billingClaims, loadClaims, paginationSettings]
    );

    useEffect(
        () => document.getElementsByClassName(style.billingHistoryCollapsibleHeader)[0].scrollIntoView(),
        [isCollapsed]
    );

    useEffect(
        () => {
            const loadServiceAndFeeCodeDetails = async (): Promise<void> => {
                if (!billingClaims?.any())
                    return;

                const uniqueDiagnosticCodes = []
                    .concat(...(billingClaims?.map(claim => claim?.DiagnosticCodes) ?? []))
                    .distinct();

                const uniqueServiceFeeCodes = []
                    .concat(...(billingClaims?.map(claim => claim?.Codes) ?? []))
                    .distinct();

                if (!uniqueDiagnosticCodes.any() && !uniqueServiceFeeCodes.any())
                    return;

                const diagnosticCodesHaveChanged = Array.from(uniqueDiagnosticCodesRef.current).sort((a, b) => a.localeCompare(b)).join()
                    !== Array.from(uniqueDiagnosticCodes).sort((a, b) => a.localeCompare(b)).join();

                const serviceCodeFeesHaveChanged = Array.from(uniqueServiceFeeCodesRef.current).sort((a, b) => a.localeCompare(b)).join()
                    !== Array.from(uniqueServiceFeeCodes).sort((a, b) => a.localeCompare(b)).join();

                if (!diagnosticCodesHaveChanged && !serviceCodeFeesHaveChanged)
                    return;

                uniqueDiagnosticCodesRef.current = uniqueDiagnosticCodes;
                uniqueServiceFeeCodesRef.current = uniqueServiceFeeCodes;

                const response = await loadDiagnosticCodeAndServiceFeeDetails(
                    uniqueDiagnosticCodes,
                    uniqueServiceFeeCodes
                );

                if (response) {
                    setCodeDetails({
                        diagnosticCodeNames: response.DiagnosticCodeNames,
                        serviceFeeDescriptions: response.ServiceFeeDescriptions
                    });
                }
            };

            loadServiceAndFeeCodeDetails();
        },
        [billingClaims, loadDiagnosticCodeAndServiceFeeDetails]
    );

    const onPrint = async (event: React.MouseEvent): Promise<void> => {
        event.stopPropagation();

        const response = await patientStore
            .ActionsMedicalNote
            .GeneratePatientBillingHistoryPdfAsync(
                patientStore.getCurrentPatient().InactivableResponse.Id,
                paginationSettings.OrderType,
                paginationSettings.OrderDirection
            );

        if (response.IsSuccess)
            PrintHelper.ShowPrintPreviewFileName(response.FileName);
    };

    const onPageChange = (nextPageNumber: number): void => {
        loadClaims({ ...paginationSettings, Offset: ((nextPageNumber - 1) * paginationSettings.PageSize) });
    };

    const onSort = (nextPatientBillingHistoryOrderType: PatientBillingHistoryOrderType): void => {
        if (paginationSettings.OrderType === nextPatientBillingHistoryOrderType)
            sortDirectionRef.current = sortDirectionRef.current != SortDirection.DESC
                ? sortDirectionRef.current = SortDirection.DESC
                : sortDirectionRef.current = SortDirection.ASC;

        loadClaims({
            ...paginationSettings,
            OrderType: nextPatientBillingHistoryOrderType,
            OrderDirection: sortDirectionRef.current
        });
    };

    const getCodeDescription = (code: string): string => codeDetails.diagnosticCodeNames[code] ?? "";

    const getServiceFeeDescription = (codes: string[]): string => codeDetails.serviceFeeDescriptions[codes[0]] ?? "";

    const isOverdue = (code: string, id: string): boolean => overdueClaimDetails
        .any(overdueClaim =>
            overdueClaim.providerClaimId == id
            && overdueClaim.code == code
        );

    const getSortArrowForColumn = (column: PatientBillingHistoryOrderType): JSX.Element => {
        if (column !== paginationSettings.OrderType)
            return <></>;

        return sortDirectionRef.current != SortDirection.ASC
            ? <div className={`${style.thIcon}`}><Img_icon_table_downarrow onClick={() => onSort(column)} /></div>
            : <div className={`${style.thIcon}`}><Img_icon_table_uparrow onClick={() => onSort(column)} /></div>;
    };

    const site = cacheStore.GetSiteById(props.encounterSiteId);
    const billingProvider = sessionStore.getCurrentEmployee().getBillingProvider();

    // Diagnostic codes ICD9 MOH enabled
    const isMOHICD9Enabled = billingProvider == BillingProvider.ClinicAid
        && site.isSiteInProvince(CanadaState.Ontario);

    return (
        <>
            <div
                className={style.billingHistoryCollapsibleHeader}
                onClick={() => setIsCollapsed(currentValue => !currentValue)}
            >
                <span>{getText("BillingHistoryLabel")}</span>
                <div>
                    {!props.isReadOnly && (<a onClick={e => onPrint(e)}>{getText("PrintLabel")}</a>)}
                    <span className={style.expandArrow}>{isCollapsed ? <span>&#x25BE;</span> : <span>&#x25B4;</span>}</span>
                </div>
            </div>

            <div className={style.billingHistoryCollapsibleHeaderContent}>
                {!isCollapsed && (
                    <>
                        <div className={`${style.billingHistoryItem} ${style.billingHistoryDivider}`}>
                            <a onClick={() => onSort(PatientBillingHistoryOrderType.ClaimDate)}>{getText("LastBilledLabel")} {getSortArrowForColumn(PatientBillingHistoryOrderType.ClaimDate)}</a>
                            <a onClick={() => onSort(PatientBillingHistoryOrderType.ProviderFullName)}>{getText("ProviderLabel")} {getSortArrowForColumn(PatientBillingHistoryOrderType.ProviderFullName)}</a>
                            <span>{getText("FeeCodeLabel")}</span>
                            <span>{isMOHICD9Enabled ? getText("MOHICD9Label") : getText("ICD9Label")}</span>
                        </div>

                        {billingClaims?.map(claim => {
                            const lastBilledDisplayDate = new DateOnly(claim.ClaimDate).toString();
                            const ICD9Codes = claim.DiagnosticCodes;
                            const canClickOnClaim = ExternalBillingManager.canEmployeeViewClaimDetails(sessionStore.getCurrentEmployee(), claim) && !props.isReadOnly;

                            return (
                                <div
                                    className={`${style.billingHistoryItem} ${style.billingHistoryClaim} ${canClickOnClaim ? style.clickableBillingHistoryClaim : ""}`}
                                    onClick={() => {
                                        if (canClickOnClaim) ExternalBillingManager.openClaimWindowAsync(
                                            claim,
                                            () => { sessionStore.dispatch(LocalPatientOperations.MedicalNote.ExternalBilling_IncrementPatientBillingHistoryVersion); }
                                        );
                                    }}
                                    key={`${claim.ProviderClaimId}_claim`}
                                >
                                    <span>{lastBilledDisplayDate}</span>
                                    <span>{claim.ProviderDisplayName}</span>
                                    <span>{`${claim.Codes} - ${getServiceFeeDescription(claim.Codes)}`}</span>

                                    <span>{ICD9Codes?.map(code => (
                                        <p
                                            className={isOverdue(code, claim.ProviderClaimId) ? style.overdueBillingHistoryItem : ""}
                                            key={`${claim.ProviderClaimId}_${code}_Code`}
                                        >
                                            {isMOHICD9Enabled && !getCodeDescription(code) ? "-" : `${code} - ${getCodeDescription(code)}`}
                                        </p>
                                    ))}
                                    </span>
                                </div>
                            );
                        })}
                        <div className={`${style.billingHistoryDivider}`} />
                    </>
                )}
            </div>

            {!isCollapsed && (
                <div
                    className={style.billingHistoryFooter}
                >
                    <div>
                        <SimplePagerControl
                            PageSize={paginationSettings?.PageSize}
                            TotalRows={paginationSettings?.TotalCount}
                            CurrentRowOffset={paginationSettings?.Offset}
                            changePageFunction={onPageChange}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export { PatientBillingHistory };
